import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/design/components';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errors';
import { formatDate, WEEKDAYS } from '@/lib/dates';
import { REPEAT_LABEL, type TaskRepeat, type TaskValues } from './taskValues';

/** תאריך ושעה בפורמט ש input datetime-local מצפה לו. */
function localValue(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
    date.getHours(),
  )}:${pad(date.getMinutes())}`;
}

/**
 * קיצורי יעד.
 *
 * "עד מתי" הוא השדה שהכי מרתיע מלמלא, ובלעדיו אין איחור ואין
 * התראה. שלוש לחיצות מכסות כמעט כל משימה, ומי שצריך משהו אחר
 * פותח את השדה עצמו.
 *
 * סוף היום ולא עכשיו: "היום" פירושו עד סוף היום.
 */
const QUICK_DUE: { label: string; at: () => Date }[] = [
  {
    label: 'היום',
    at: () => {
      const day = new Date();
      day.setHours(23, 59, 0, 0);
      return day;
    },
  },
  {
    label: 'מחר',
    at: () => {
      const day = new Date();
      day.setDate(day.getDate() + 1);
      day.setHours(23, 59, 0, 0);
      return day;
    },
  },
  {
    label: 'השבוע',
    at: () => {
      // סוף השבוע הוא שבת, והשבוע מתחיל בראשון (PRD 2.2)
      const day = new Date();
      day.setDate(day.getDate() + (6 - day.getDay()));
      day.setHours(23, 59, 0, 0);
      return day;
    },
  },
];

/**
 * משימה: על מי, עד מתי, ומה בדיוק.
 *
 * בחלון ולא בשורה, ואותו חלון ליצירה ולעריכה. קודם אפשר היה רק
 * ליצור, ומשימה שנוסחה לא נכון או שהוטלה על מי שלא צריך חייבה
 * למחוק ולכתוב מחדש — וזה גם מוחק את ההיסטוריה שלה.
 */
export function TaskDialog({
  initial,
  branches,
  employees,
  priorities,
  isSaving,
  onSave,
  onClose,
}: {
  initial: TaskValues;
  branches: { id: string; name: string; color?: string | null }[];
  employees: { id: string; first_name: string; last_name: string; primary_branch_id: string | null }[];
  priorities: { id: string; name: string }[];
  isSaving: boolean;
  onSave: (values: TaskValues) => Promise<unknown>;
  onClose: () => void;
}) {
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);

  const set = <K extends keyof TaskValues>(key: K, value: TaskValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));

  // בחירת עובד מצמצמת את הרשימה לסניף שנבחר, אם נבחר
  const forBranch = values.branchId
    ? employees.filter((one) => one.primary_branch_id === values.branchId)
    : employees;

  return (
    <div className="fixed inset-0 z-40 grid items-end bg-charcoal/50 p-0 sm:place-items-center sm:p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={initial.id ? 'עריכת משימה' : 'משימה חדשה'}
        className="grid max-h-[90dvh] w-full gap-4 overflow-y-auto rounded-t-lg bg-surface p-4 sm:max-w-lg sm:rounded-lg"
      >
        <div className="flex items-start justify-between gap-2">
          <h2 className="m-0 font-display text-display-m font-semibold">
            {initial.id ? 'עריכת משימה' : 'משימה חדשה'}
          </h2>
          <button
            type="button"
            aria-label="סגירה"
            onClick={onClose}
            className="grid size-11 shrink-0 cursor-pointer place-items-center rounded-sm bg-transparent text-ink hover:bg-cream"
          >
            <X size={20} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}

        <form
          className="grid gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            setError(null);
            void onSave(values)
              .then(onClose)
              .catch((caught: unknown) => setError(errorMessage(caught)));
          }}
        >
          <label className="grid gap-1">
            <span className="text-label font-medium">מה צריך לעשות</span>
            <input
              autoFocus
              value={values.title}
              onChange={(event) => set('title', event.target.value)}
              placeholder="למשל: להחליף תאורה בחלון"
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink placeholder:text-muted"
            />
          </label>

          <label className="grid gap-1">
            <span className="text-label font-medium">פירוט</span>
            <textarea
              value={values.description}
              rows={2}
              onChange={(event) => set('description', event.target.value)}
              className="rounded-sm border border-muted bg-surface p-2 text-body text-ink"
            />
          </label>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="text-label font-medium">סניף</span>
              <select
                value={values.branchId}
                onChange={(event) => {
                  set('branchId', event.target.value);
                  // עובד מסניף אחר כבר אינו רלוונטי
                  const stays = employees.find(
                    (one) =>
                      one.id === values.assigneeId &&
                      (!event.target.value || one.primary_branch_id === event.target.value),
                  );
                  if (!stays) set('assigneeId', '');
                }}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
              >
                <option value="">כל הסניפים</option>
                {branches.map((branch) => (
                  <option key={branch.id} value={branch.id}>
                    {branch.name}
                  </option>
                ))}
              </select>
            </label>

            <label className="grid gap-1">
              <span className="text-label font-medium">על מי</span>
              <select
                value={values.assigneeId}
                onChange={(event) => set('assigneeId', event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
              >
                {/*
                  משימה בלי אחראי היא משימה של הסניף, ומי שנמצא
                  שם לוקח אותה. זו אפשרות ולא חוסר.
                */}
                <option value="">כל מי שבסניף</option>
                {forBranch.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.first_name} {employee.last_name}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <fieldset className="m-0 grid gap-2 border-0 p-0">
            <legend className="p-0 text-label font-medium">חזרתיות</legend>
            <div className="grid gap-1">
              {(Object.keys(REPEAT_LABEL) as TaskRepeat[]).map((option) => (
                <label key={option} className="flex min-h-11 items-center gap-2 text-body">
                  <input
                    type="radio"
                    name="repeat"
                    checked={values.repeat === option}
                    onChange={() => set('repeat', option)}
                    className="size-5"
                  />
                  {REPEAT_LABEL[option]}
                </label>
              ))}
            </div>

            {values.repeat === 'weekly' && (
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((name, index) => {
                  const active = values.weekdays.includes(index);
                  return (
                    <button
                      key={name}
                      type="button"
                      aria-pressed={active}
                      onClick={() =>
                        setValues((current) => ({
                          ...current,
                          weekdays: current.weekdays.includes(index)
                            ? current.weekdays.filter((one) => one !== index)
                            : [...current.weekdays, index],
                        }))
                      }
                      className={cn(
                        'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm transition-colors',
                        active
                          ? 'border-ink bg-olive font-semibold text-ink'
                          : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                      )}
                    >
                      {name}
                    </button>
                  );
                })}
              </div>
            )}

            {values.repeat === 'monthly' && (
              <label className="grid max-w-40 gap-1">
                <span className="text-label text-muted">יום בחודש</span>
                <input
                  type="number"
                  min={1}
                  max={31}
                  dir="ltr"
                  value={values.dayOfMonth ?? ''}
                  onChange={(event) =>
                    set('dayOfMonth', event.target.value ? Number(event.target.value) : null)
                  }
                  className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
                />
              </label>
            )}

            {/*
              משימה קיימת שהופכת לחוזרת: התבנית תיצור אותה מחדש
              בכל פעם שהיא חלה, והמשימה הבודדת שכבר קיימת מיותרת.
              נאמר במפורש, כי היעלמות של שורה בלי הסבר נראית כמו
              תקלה.
            */}
            {initial.id !== null && values.repeat !== 'once' && (
              <p className="m-0 rounded-sm border border-line bg-cream p-2 text-body-sm text-ink">
                המשימה תהפוך לתבנית חוזרת, והשורה הנוכחית תוחלף במשימה שתיווצר ממנה.
              </p>
            )}

            {values.repeat !== 'once' && (
              <>
                <label className="grid max-w-40 gap-1">
                  <span className="text-label text-muted">עד איזו שעה</span>
                  <input
                    type="time"
                    dir="ltr"
                    value={values.dueTime}
                    onChange={(event) => set('dueTime', event.target.value)}
                    className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
                  />
                </label>
                <p className="m-0 text-body-sm text-muted">
                  המשימה נוצרת מחדש בכל פעם שהיא חלה. מה שלא בוצע נסגר בסוף היום ולא מצטבר.
                </p>
              </>
            )}
          </fieldset>

          {values.repeat === 'once' && (
          <fieldset className="m-0 grid gap-2 border-0 p-0">
            <legend className="p-0 text-label font-medium">עד מתי</legend>
            <div className="flex flex-wrap gap-2">
              {QUICK_DUE.map((option) => {
                const at = localValue(option.at());
                const active = values.dueAt === at;
                return (
                  <button
                    key={option.label}
                    type="button"
                    aria-pressed={active}
                    onClick={() => set('dueAt', active ? '' : at)}
                    className={cn(
                      'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm transition-colors',
                      active
                        ? 'border-ink bg-olive font-semibold text-ink'
                        : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                    )}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            <input
              type="datetime-local"
              dir="ltr"
              value={values.dueAt}
              onChange={(event) => set('dueAt', event.target.value)}
              className="min-h-11 max-w-64 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
            />
            <p className="m-0 text-body-sm text-muted">
              {values.dueAt
                ? `עד ${formatDate(values.dueAt)}, ${values.dueAt.slice(11, 16)}`
                : 'ריק = בלי תאריך יעד, ובלי איחור.'}
            </p>
          </fieldset>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="grid gap-1">
              <span className="text-label font-medium">דחיפות</span>
              <select
                value={values.priorityId}
                onChange={(event) => set('priorityId', event.target.value)}
                className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-body text-ink"
              >
                {/*
                  ניסוח שאינו יכול להתנגש בערך מהטבלה. "רגילה"
                  כאן הופיע פעמיים, כי זה גם שם של דחיפות קיימת
                  וגם היה הניסוח של האפשרות הריקה (כלל 1).
                */}
                <option value="">ללא דחיפות</option>
                {priorities.map((priority) => (
                  <option key={priority.id} value={priority.id}>
                    {priority.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="grid content-end gap-1">
              <label className="flex min-h-11 items-center gap-2 text-body">
                <input
                  type="checkbox"
                  checked={values.requiresPhoto}
                  onChange={(event) => set('requiresPhoto', event.target.checked)}
                  className="size-5"
                />
                דורשת צילום
              </label>

              {/*
                ברירת המחדל היא לא. משימה יומית שחוזרת בכל סניף
                היתה מייצרת עשרות התראות ביום, והמנהל היה מכבה
                את כולן — כולל את זו שבאמת חשובה לו.
              */}
              <label className="flex min-h-11 items-center gap-2 text-body">
                <input
                  type="checkbox"
                  checked={values.notifyOnComplete}
                  onChange={(event) => set('notifyOnComplete', event.target.checked)}
                  className="size-5"
                />
                להודיע לי כשבוצעה
              </label>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              variant="primary"
              disabled={
                isSaving ||
                values.title.trim() === '' ||
                (values.repeat === 'weekly' && values.weekdays.length === 0) ||
                (values.repeat === 'monthly' && values.dayOfMonth === null)
              }
            >
              {initial.id ? 'שמירת השינויים' : 'יצירת המשימה'}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              ביטול
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
